import type { PollInsights, Slot, VoteType } from "./types";

export interface EngagementParticipant {
  id: string;
  name: string;
  votes: Record<string, VoteType>;
}

export interface EngagementRanked extends EngagementParticipant {
  /** Number of slots the participant voted on (yes/maybe/no) */
  votedCount: number;
  /** Number of slots voted "yes" */
  yesCount: number;
  /** Engagement score : votedCount*10 + yesCount (higher = more engaged) */
  score: number;
  /** True iff the participant has zero votes across all slots */
  isPending: boolean;
}

/**
 * Partitionne participants en "actifs" (au moins un vote) et "en attente"
 * (aucun vote sur aucun slot). Tri stable par engagement décroissant ;
 * tie-break alphabétique (FR locale) sur le nom pour stabilité.
 *
 * Règles :
 * - votedCount = nombre de slots avec un vote ∈ {yes,maybe,no}
 * - yesCount = nombre de slots votés "yes"
 * - score = votedCount * 10 + yesCount (votedCount domine, yesCount départage)
 * - isPending ⇔ votedCount === 0
 *
 * Important : le tri est stable, donc deux participants au même score
 * conservent l'ordre alphabétique.
 */
export interface EngagementPartition {
  active: EngagementRanked[];
  pending: EngagementRanked[];
}

export function rankByEngagement(
  participants: EngagementParticipant[],
  slots: Slot[],
  _insights?: PollInsights, // reserved for future weighting
): EngagementPartition {
  const slotIds = slots.map((s) => s.id);
  const ranked: EngagementRanked[] = participants.map((p) => {
    let voted = 0;
    let yes = 0;
    for (const id of slotIds) {
      const v = p.votes[id];
      if (v === "yes" || v === "maybe" || v === "no") {
        voted += 1;
        if (v === "yes") yes += 1;
      }
    }
    return {
      ...p,
      votedCount: voted,
      yesCount: yes,
      score: voted * 10 + yes,
      isPending: voted === 0,
    };
  });

  const active = ranked
    .filter((r) => !r.isPending)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.name.localeCompare(b.name, "fr");
    });

  const pending = ranked
    .filter((r) => r.isPending)
    .sort((a, b) => a.name.localeCompare(b.name, "fr"));

  return { active, pending };
}
